import { useCallback, useMemo, useState } from 'react';
import {
  createStarsDonationInvoice,
  getPaymentOrderStatus,
  type PaymentOrderStatus,
} from '../lib/api';
import { getTelegramInitData } from '../lib/telegram';
import { haptic } from '../utils/haptic';

type PurchaseState =
  | 'idle'
  | 'loading'
  | 'pending_verification'
  | 'canceled'
  | 'failed'
  | 'success';

function parsePositiveInt(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function parsePresetAmounts(value: string | undefined): number[] {
  const raw = String(value || '100,300,500')
    .split(',')
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => Number.isFinite(item) && item > 0);
  const unique = Array.from(new Set(raw));
  return unique.length > 0 ? unique : [100, 300, 500];
}

const MIN_AMOUNT_XTR = parsePositiveInt(import.meta.env.VITE_STARS_DONATION_MIN_XTR, 1);
const MAX_AMOUNT_XTR = parsePositiveInt(import.meta.env.VITE_STARS_DONATION_MAX_XTR, 10000);
const PRESET_AMOUNTS = parsePresetAmounts(import.meta.env.VITE_STARS_DONATION_PRESETS);
const DEFAULT_AMOUNT = PRESET_AMOUNTS[0] || 100;

function getWebApp() {
  return window.Telegram?.WebApp;
}

function invoiceStatusText(status: string): string {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'paid') return 'Спасибо за поддержку проекта.';
  if (normalized === 'cancelled' || normalized === 'canceled') {
    return 'Счет закрыт без оплаты.';
  }
  if (normalized === 'failed') return 'Платеж не прошел. Попробуйте еще раз.';
  return 'Проверяем статус оплаты...';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function StarsCheckoutCard() {
  const [state, setState] = useState<PurchaseState>('idle');
  const [statusText, setStatusText] = useState('Поддержите проект любой удобной суммой в Telegram Stars.');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [amountXtr, setAmountXtr] = useState<number | null>(null);
  const [amountInput, setAmountInput] = useState<string>(String(DEFAULT_AMOUNT));

  const selectedAmount = useMemo(() => {
    const parsed = Number.parseInt(amountInput.trim(), 10);
    if (!Number.isFinite(parsed)) return null;
    if (parsed < MIN_AMOUNT_XTR || parsed > MAX_AMOUNT_XTR) return null;
    return parsed;
  }, [amountInput]);

  const verifyOrder = useCallback(
    async (currentOrderId: string): Promise<PaymentOrderStatus | null> => {
      setState('pending_verification');
      setStatusText('Платеж обрабатывается, проверяем заказ...');

      for (let attempt = 0; attempt < 12; attempt += 1) {
        try {
          const status = await getPaymentOrderStatus(currentOrderId);
          if (status.status === 'paid') {
            setState('success');
            setStatusText('Спасибо, платеж подтвержден.');
            haptic.success();
            return status;
          }
          if (status.status === 'canceled' || status.status === 'cancelled') {
            setState('canceled');
            setStatusText('Счет закрыт без оплаты.');
            return status;
          }
          if (status.status === 'failed' || status.status === 'refunded') {
            setState('failed');
            setStatusText('Платеж не завершен. Попробуйте снова.');
            return status;
          }
        } catch {
          // backend may still be processing update; keep polling briefly
        }
        await sleep(1500);
      }

      setState('pending_verification');
      setStatusText('Ожидаем подтверждение оплаты. Обновите экран через несколько секунд.');
      return null;
    },
    [],
  );

  const onBuyClick = useCallback(async () => {
    if (!selectedAmount) {
      setState('failed');
      setStatusText(`Введите сумму от ${MIN_AMOUNT_XTR} до ${MAX_AMOUNT_XTR} XTR.`);
      return;
    }

    const initData = getTelegramInitData();
    if (!initData) {
      setState('failed');
      setStatusText('Откройте Mini App внутри Telegram, чтобы оплатить через Stars.');
      return;
    }

    setState('loading');
    setStatusText('Создаем счет в Telegram Stars...');
    haptic.action();

    try {
      const invoice = await createStarsDonationInvoice({
        initData,
        amountXtr: selectedAmount,
      });
      setOrderId(invoice.order_id);
      setAmountXtr(invoice.amount_xtr);

      const tg = getWebApp();
      if (!tg?.openInvoice) {
        setState('pending_verification');
        setStatusText('Открываем счет в браузере, затем проверяем статус...');
        window.open(invoice.invoice_link, '_blank', 'noopener,noreferrer');
        await verifyOrder(invoice.order_id);
        return;
      }

      setState('pending_verification');
      setStatusText('Счет открыт. Завершите оплату в Telegram.');

      let verificationStarted = false;
      const runVerificationOnce = () => {
        if (verificationStarted) return;
        verificationStarted = true;
        void verifyOrder(invoice.order_id);
      };

      const onInvoiceClosed = (eventPayload?: unknown) => {
        const status =
          typeof eventPayload === 'object' &&
          eventPayload !== null &&
          'status' in eventPayload &&
          typeof (eventPayload as { status?: unknown }).status === 'string'
            ? String((eventPayload as { status: string }).status)
            : 'pending';
        setStatusText(invoiceStatusText(status));
        runVerificationOnce();
      };

      tg.onEvent?.('invoiceClosed', onInvoiceClosed);
      tg.openInvoice(invoice.invoice_link, (status) => {
        setStatusText(invoiceStatusText(status));
        runVerificationOnce();
      });

      window.setTimeout(() => {
        tg.offEvent?.('invoiceClosed', onInvoiceClosed);
        runVerificationOnce();
      }, 8000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось создать счет';
      setState('failed');
      setStatusText(message);
      haptic.error();
    }
  }, [selectedAmount, verifyOrder]);

  const statusClassName = `stars-checkout-status stars-checkout-status--${state}`;
  const buyDisabled = state === 'loading' || state === 'pending_verification' || selectedAmount === null;

  return (
    <div className="stars-checkout-card">
      <div className="stars-checkout-head">
        <div>
          <div className="stars-checkout-title">Поддержать проект</div>
          <div className="stars-checkout-subtitle">
            {amountXtr ? `Последний счет: ${amountXtr} XTR` : 'Выберите сумму поддержки'}
          </div>
        </div>
      </div>

      <div className="stars-amounts-row">
        {PRESET_AMOUNTS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={`stars-amount-chip ${selectedAmount === preset ? 'is-active' : ''}`}
            onClick={() => setAmountInput(String(preset))}
            disabled={state === 'loading' || state === 'pending_verification'}
          >
            {preset} XTR
          </button>
        ))}
      </div>

      <label className="stars-custom-amount">
        <span className="stars-custom-amount-label">Своя сумма</span>
        <input
          type="number"
          min={MIN_AMOUNT_XTR}
          max={MAX_AMOUNT_XTR}
          step={1}
          inputMode="numeric"
          className="stars-custom-amount-input"
          value={amountInput}
          onChange={(event) => setAmountInput(event.target.value)}
          disabled={state === 'loading' || state === 'pending_verification'}
        />
      </label>

      <button
        type="button"
        className="stars-checkout-button"
        onClick={() => void onBuyClick()}
        disabled={buyDisabled}
      >
        {state === 'loading' ? 'Создаем счет...' : 'Поддержать через Telegram Stars'}
      </button>

      <div className={statusClassName}>{statusText}</div>
      {orderId && <div className="stars-checkout-meta">order: {orderId}</div>}
    </div>
  );
}
